import EmailIcon from "@mui/icons-material/Email";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  Grid,
  Grow,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { green } from "@mui/material/colors";
import axios from "axios";
import { Formik } from "formik";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { object, string } from "yup";
import { baseUrl } from "../api/API";
import { userRole } from "../constant/userConfig";
import useAuthStore from "../store/authStore";

const Login = () => {
  const [loginError, setLoginError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const theme = useTheme();

  // Schéma de validation
  const userSchema = object({
    email: string()
      .email("Format de l'e-mail incorrect")
      .required("L'adresse e-mail est obligatoire"),
    password: string()
      .required("Le mot de passe est obligatoire")
      .min(6, "Le mot de passe doit contenir au moins 6 caractères"),
  });

  const HandleNAvigation = (role) => {
    if (
      [
        userRole.admin_assurance,
        userRole.agent_assurance,
        userRole.medecin_assurance,
      ].includes(role)
    ) {
      navigate("/assurance");
    }
    if (
      [
        userRole.admin_clinique,
        userRole.agent_clinique,
        userRole.medecin_clinique,
      ].includes(role)
    ) {
      navigate("/clinique");
    }
    if ([userRole.admin_pharmacie, userRole.agent_pharmacie].includes(role)) {
      navigate("/pharmacie");
    }
    if (
      [userRole.admin_laboratoire, userRole.agent_laboratoire].includes(role)
    ) {
      navigate("/labo");
    }
    if (
      [
        userRole.admin_clinique_laboratoire,
        userRole.agent_clinique_laboratoire,
        userRole.medecin_clinique_laboratoire,
      ].includes(role)
    ) {
      navigate("/clinique-labo");
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e4efe9 100%)",
        padding: 0,
      }}
    >
      <Grid container sx={{ height: "100%" }}>
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: { xs: "none", md: "flex" },
            backgroundImage: "url('/assets/hands.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(46, 125, 50, 0.7)",
            },
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              zIndex: 1,
              textAlign: "center",
              color: "white",
              p: 4,
              maxWidth: "80%",
            }}
          >
            <Fade in={true} timeout={1000}>
              <Typography variant="h2" fontWeight="bold" mb={3}>
                LaafiSeebre
              </Typography>
            </Fade>
            <Fade in={true} timeout={1500}>
              <Typography variant="h5" mb={4}>
                Plateforme de gestion des soins de santé pour les assureurs,
                hôpitaux, pharmacies et laboratoires
              </Typography>
            </Fade>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 2, sm: 4, md: 6 },
          }}
        >
          <Grow in={true} timeout={800}>
            <Paper
              elevation={8}
              sx={{
                p: { xs: 3, sm: 4 },
                width: "100%",
                maxWidth: "450px",
                borderRadius: 2,
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 4,
                }}
              >
                <Avatar
                  sx={{
                    width: 70,
                    height: 70,
                    bgcolor: green[600],
                    mb: 2,
                    boxShadow: "0 4px 12px rgba(46, 125, 50, 0.2)",
                  }}
                >
                  <LocalHospitalIcon fontSize="large" />
                </Avatar>
                <Typography
                  variant="h4"
                  component="h1"
                  align="center"
                  fontWeight="600"
                  color="text.primary"
                >
                  Connexion
                </Typography>
                <Typography
                  variant="body1"
                  align="center"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Accédez à votre espace personnel
                </Typography>
              </Box>

              <Formik
                validationSchema={userSchema}
                onSubmit={async (values, { setSubmitting }) => {
                  setLoginError(null);
                  try {
                    const resp = await axios.post(
                      `${baseUrl}/auth/login`,
                      values,
                      {
                        withCredentials: true,
                      }
                    );

                    if (resp.status === 200) {
                      const { user, token } = resp.data;
                      const { expiresAt } = resp.data.tokenDetails;
                      login(user, token, expiresAt);
                      HandleNAvigation(user.role);
                    }
                  } catch (error) {
                    setLoginError(
                      error.response?.data?.message ||
                        "Erreur de connexion. Veuillez vérifier vos identifiants."
                    );
                  }
                  setSubmitting(false);
                }}
                initialValues={{ email: "", password: "" }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                }) => (
                  <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                  >
                    <TextField
                      label="Adresse Email"
                      name="email"
                      variant="outlined"
                      fullWidth
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                      }}
                    />
                    <TextField
                      label="Mot de passe"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      variant="outlined"
                      fullWidth
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                      }}
                    />

                    {loginError && (
                      <Grow in={Boolean(loginError)}>
                        <Alert
                          severity="error"
                          sx={{
                            borderRadius: 1,
                            "& .MuiAlert-icon": { alignItems: "center" },
                          }}
                        >
                          {loginError}
                        </Alert>
                      </Grow>
                    )}

                    <Button
                      type="submit"
                      variant="contained"
                      color="success"
                      fullWidth
                      disabled={isSubmitting}
                      sx={{
                        mt: 1,
                        py: 1.5,
                        bgcolor: green[600],
                        "&:hover": {
                          bgcolor: green[700],
                        },
                        position: "relative",
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress
                          size={24}
                          sx={{
                            color: green[500],
                          }}
                        />
                      ) : (
                        "Se connecter"
                      )}
                    </Button>

                    <Box
                      textAlign="center"
                      sx={{
                        mt: 2,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Link
                        to="/change-password"
                        style={{
                          textDecoration: "none",
                          color: theme.palette.primary.main,
                          transition: "color 0.3s ease",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            "&:hover": {
                              color: theme.palette.primary.dark,
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Mot de passe oublié ?
                        </Typography>
                      </Link>
                    </Box>
                  </Box>
                )}
              </Formik>
            </Paper>
          </Grow>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Login;
